import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "components/ui/Button";
import { SHARED_RECORD_TYPES, buildSharingFormValues } from "utils/sharing";
import SharingRow from "components/sharing/SharingRow";

export default function OfficeEdit({ initialData = {}, onSubmit }) {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialData.image || null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData.name || "",
      aiAssistant: {
        name: initialData.aiAssistant?.name || "",
        picture: initialData.aiAssistant?.picture || "",
      },
      officeId: initialData.officeId || "",
      domainId: initialData.domainId || "",
      reaId: initialData.reaId || "",
      address: initialData.address || "",
      suburb: initialData.suburb || "",
      phone: initialData.phone || "",
      email: initialData.email || "",
      caId: initialData.caId || "",
      caOfficeId: initialData.caOfficeId || "",
      smsUser: initialData.smsUser || "",
      smsPass: initialData.smsPass || "",
      amlUsername: initialData.amlUsername || "",
      amlPassword: initialData.amlPassword || "",
      cfClientId: initialData.cfClientId || "",
      cfClientSecret: initialData.cfClientSecret || "",
      cfTenantId: initialData.cfTenantId || "",
      cfWebhookSecret: initialData.cfWebhookSecret || "",
      sharing: buildSharingFormValues(initialData.sharing),
    },
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const submitForm = (data) => {
    const transformedData = {
      ...data,
      domainId: parseInt(data.domainId),
      imageFile,
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Office Name *
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: "Office name is required" })}
          className={`form-input border ${
            errors.name ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.name && (
          <p className="form-error-message">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="officeId" className="form-label">
          Office ID *
        </label>
        <input
          type="text"
          id="officeId"
          {...register("officeId", { required: "Office ID is required" })}
          className={`form-input border ${
            errors.officeId ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.officeId && (
          <p className="form-error-message">{errors.officeId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="aiAssistant.name" className="form-label">
          Assistant name
        </label>
        <input
          type="text"
          id="aiAssistant.name"
          maxLength={60}
          placeholder="Leave blank to inherit from the group"
          {...register("aiAssistant.name", {
            maxLength: {
              value: 60,
              message: "Assistant name must be 60 characters or fewer",
            },
          })}
          className={`form-input border ${
            errors.aiAssistant?.name ? "border-red-500" : "border-mediumgray"
          }`}
        />
        <p className="text-xs text-darkgray">
          This is the most specific assistant name for agents in this office.
        </p>
        {errors.aiAssistant?.name && (
          <p className="form-error-message">{errors.aiAssistant.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="aiAssistant.picture" className="form-label">
          Assistant photo URL
        </label>
        <input
          type="url"
          id="aiAssistant.picture"
          placeholder="Leave blank to inherit from the group"
          {...register("aiAssistant.picture")}
          className="form-input border border-mediumgray"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="domainId" className="form-label">
          Domain ID *
        </label>
        <input
          type="number"
          id="domainId"
          {...register("domainId", {
            required: "Domain ID is required",
            min: {
              value: 1,
              message: "Domain ID must be a positive number",
            },
          })}
          className={`form-input border ${
            errors.domainId ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.domainId && (
          <p className="form-error-message">{errors.domainId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="reaId" className="form-label">
          REA ID *
        </label>
        <input
          type="text"
          id="reaId"
          {...register("reaId", { required: "REA ID is required" })}
          className={`form-input border ${
            errors.reaId ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.reaId && (
          <p className="form-error-message">{errors.reaId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="address" className="form-label">
          Address *
        </label>
        <input
          type="text"
          id="address"
          {...register("address", { required: "Address is required" })}
          className={`form-input border ${
            errors.address ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.address && (
          <p className="form-error-message">{errors.address.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="suburb" className="form-label">
          Suburb *
        </label>
        <input
          type="text"
          id="suburb"
          {...register("suburb", { required: "Suburb is required" })}
          className={`form-input border ${
            errors.suburb ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.suburb && (
          <p className="form-error-message">{errors.suburb.message}</p>
        )}
      </div>

      {/* Not editable on purpose — which law applies is decided by where the
          office is, so it is derived from the suburb when the office saves. */}
      <div className="space-y-2">
        <span className="form-label">Jurisdiction</span>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              initialData?.jurisdiction
                ? "bg-blue-100 text-blue-800"
                : "bg-mediumgray text-darkergray"
            }`}
          >
            {initialData?.jurisdiction || "Not set"}
          </span>
          <span className="text-xs text-darkgray">
            Set from the suburb when you save.
          </span>
        </div>
      </div>

      {/* Printed on this office's letterhead, brochures and signboards —
          left blank, the artwork prints blank. */}
      <div className="space-y-2">
        <label htmlFor="phone" className="form-label">
          Office Phone
        </label>
        <input
          type="text"
          id="phone"
          {...register("phone")}
          className="form-input border border-mediumgray"
          placeholder="Printed on letterheads, brochures and signboards"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="form-label">
          Office Email
        </label>
        <input
          type="text"
          id="email"
          {...register("email", {
            pattern: {
              value: /^\S+@\S+\.\S+$/,
              message: "Enter a valid email address",
            },
          })}
          className={`form-input border ${
            errors.email ? "border-red-500" : "border-mediumgray"
          }`}
          placeholder="Printed on letterheads"
        />
        {errors.email && (
          <p className="form-error-message">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="caId" className="form-label">
          Campaign Agent ID *
        </label>
        <input
          type="text"
          id="caId"
          {...register("caId", { required: "Campaign Agent ID is required" })}
          className={`form-input border ${
            errors.caId ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.caId && (
          <p className="form-error-message">{errors.caId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="caOfficeId" className="form-label">
          Campaign Agent Office ID *
        </label>
        <input
          type="text"
          id="caOfficeId"
          {...register("caOfficeId", {
            required: "Campaign Agent Office ID is required",
          })}
          className={`form-input border ${
            errors.caOfficeId ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.caOfficeId && (
          <p className="form-error-message">{errors.caOfficeId.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="smsUser" className="form-label">
          SMS Username *
        </label>
        <input
          type="text"
          id="smsUser"
          {...register("smsUser", { required: "SMS username is required" })}
          className={`form-input border ${
            errors.smsUser ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.smsUser && (
          <p className="form-error-message">{errors.smsUser.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="smsPass" className="form-label">
          SMS Password *
        </label>
        <input
          type="text"
          id="smsPass"
          {...register("smsPass", { required: "SMS password is required" })}
          className={`form-input border ${
            errors.smsPass ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.smsPass && (
          <p className="form-error-message">{errors.smsPass.message}</p>
        )}
      </div>

      {/* ── AML Partners Integration ── */}
      <div className="space-y-2">
        <label htmlFor="amlUsername" className="form-label">
          AML Username *
        </label>
        <input
          type="text"
          id="amlUsername"
          {...register("amlUsername", { required: "AML username is required" })}
          className={`form-input border ${
            errors.amlUsername ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.amlUsername && (
          <p className="form-error-message">{errors.amlUsername.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="amlPassword" className="form-label">
          AML Password *
        </label>
        <input
          type="text"
          id="amlPassword"
          {...register("amlPassword", { required: "AML password is required" })}
          className={`form-input border ${
            errors.amlPassword ? "border-red-500" : "border-mediumgray"
          }`}
        />
        {errors.amlPassword && (
          <p className="form-error-message">{errors.amlPassword.message}</p>
        )}
      </div>

      {/* ── Campaign Flow Integration ── */}
      {/* Optional until the office is onboarded to Campaign Flow. */}
      <div className="space-y-2">
        <label htmlFor="cfClientId" className="form-label">
          Campaign Flow Client ID
        </label>
        <input
          type="text"
          id="cfClientId"
          {...register("cfClientId")}
          className="form-input border border-mediumgray"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="cfClientSecret" className="form-label">
          Campaign Flow Client Secret
        </label>
        <input
          type="text"
          id="cfClientSecret"
          {...register("cfClientSecret")}
          className="form-input border border-mediumgray"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="cfTenantId" className="form-label">
          Campaign Flow Tenant ID
        </label>
        <input
          type="text"
          id="cfTenantId"
          {...register("cfTenantId")}
          className="form-input border border-mediumgray"
        />
      </div>

      {/* Filled in by the backend on save: it registers this tenant's webhook
          against our receiver and stores the signing secret Campaign Flow
          generates. Kept editable only as a manual fallback. */}
      <div className="space-y-2">
        <label htmlFor="cfWebhookSecret" className="form-label">
          Campaign Flow Webhook Secret
        </label>
        <input
          type="text"
          id="cfWebhookSecret"
          {...register("cfWebhookSecret")}
          className="form-input border border-mediumgray"
          placeholder="Filled automatically when the office is saved"
        />
        <p className="text-xs text-darkgray">
          Registered automatically once Client ID, Client Secret and Tenant ID
          are saved — leave blank unless you have been given a secret to enter
          by hand.
        </p>
      </div>

      <div className="space-y-2">
        <label className="form-label">Share Across Teams</label>
        <p className="text-xs text-darkgray">
          What this office's teams can see of each other's records. A team is a
          main agent and their co-agents, and a team always shares within
          itself. Off keeps each team to its own data; turning one on never
          removes access anyone already has.
        </p>
        <div className="border border-mediumgray rounded overflow-hidden">
          {SHARED_RECORD_TYPES.map((recordType) => (
            <Controller
              key={recordType.key}
              control={control}
              name={`sharing.${recordType.key}`}
              render={({ field }) => (
                <SharingRow
                  recordType={recordType}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="image" className="form-label">
          Office Image
        </label>
        {imagePreview && (
          <div className="mb-2">
            <img
              src={imagePreview}
              alt="Office preview"
              className="w-24 h-24 object-cover rounded border border-mediumgray"
            />
          </div>
        )}
        <input
          type="file"
          id="image"
          accept="image/*"
          onChange={handleImageChange}
          className="form-input border border-mediumgray"
        />
      </div>

      <div className="flex justify-end mt-6">
        <Button type="submit" className="black-button">
          {initialData._id ? "Update Office" : "Create Office"}
        </Button>
      </div>
    </form>
  );
}
